// DOM scaffolding owned by the UI layer. Bootstrap calls these instead of
// reaching into document.getElementById('controls-panel') etc. directly.
//
// Pure DOM — no THREE, no store. Each helper is a no-op if its target isn't
// in index.html (so a swapped-in barebones HTML can omit them).

import type * as THREE from 'three';
import Stats from 'stats-gl';
import { store } from '@store/instance';
import { INTENTS } from '@store/intents';
import type { ViewerContext } from '../app/context';

const STATS_PANEL_H = 48; // stats-gl's logical per-panel height (canvas style + top offset)

/** Force any rgba()/hsla() string to full alpha. stats-gl scrolls the graph by
 *  blitting it 1px left and re-filling the new column with `bg`; a translucent
 *  fill leaves per-frame trails, so the canvas needs an opaque base even when
 *  the themed panel color (e.g. --magviz-perf-bg) is glassy. Opaque hex/rgb
 *  pass through unchanged. */
function forceOpaque(color: string): string {
    const inner = color.match(/^rgba?\(([^)]+)\)$/i)?.[1];
    if (!inner) return color;
    const [r, g, b] = inner.split(',').map((s) => s.trim());
    if (!r || !g || !b) return color;
    return `rgb(${r}, ${g}, ${b})`;
}

// stats-gl bakes each panel's colors into private fields and only repaints from
// them on initializeCanvas(); retheming means reaching those fields. Narrow
// structural view (optional → missing panels just skip, default colors kept).
type ThemedPanel = { fg: string; bg: string; initializeCanvas(): void };
type StatsPanelHost = {
    fpsPanel?: ThemedPanel;
    msPanel?: ThemedPanel;
    gpuPanel?: ThemedPanel;
    gpuPanelCompute?: ThemedPanel;
};

/** Recolor the FPS/CPU/GPU panels from the active theme's CSS vars and repaint.
 *  Returned closure re-runs on each theme apply (CSS vars are already updated by
 *  the earlier UI_THEME_APPLY listener, so a plain read is correct). */
function makeStatsThemer(stats: Stats): () => void {
    const host = stats as unknown as StatsPanelHost; // WHY: see StatsPanelHost note
    return () => {
        const css = getComputedStyle(document.documentElement);
        const accent = css.getPropertyValue('--magviz-accent').trim() || '#4ea1ff';
        const bg = forceOpaque(css.getPropertyValue('--magviz-perf-bg').trim()) || '#0f1423';
        for (const panel of [host.fpsPanel, host.msPanel, host.gpuPanel, host.gpuPanelCompute]) {
            if (!panel) continue;
            panel.fg = accent; // label + graph-gradient endpoint
            panel.bg = bg; // panel fill + gradient start (fades bg → accent)
            panel.initializeCanvas();
        }
    };
}

export function mountCanvas(renderer: THREE.WebGLRenderer): void {
    const host = document.getElementById('3d-canvas');
    if (host && renderer && renderer.domElement) host.appendChild(renderer.domElement);
}

export function setupStatsPanel(viewer: ViewerContext): () => void {
    // Always instantiate the panel (the render loop captures `stats` once at
    // start, and the Shift+P toggle needs a live instance) — initial visibility
    // is owned by the perf-container's display, not by whether stats exists.
    try {
        // Non-minimal: FPS + CPU(ms) + GPU(ms) stacked. trackGPU times the WebGL2
        // render pass via EXT_disjoint_timer_query_webgl2 (CPU-only fallback where
        // the extension is absent, e.g. Safari). No showPanel() — that hides all
        // but one; we want all three visible.
        const stats = new Stats({ trackGPU: true, horizontal: false });
        viewer.three.stats = stats;
        // init(renderer) patches renderer.render to bracket begin()/end() and sets
        // up the GPU timer. Synchronous for WebGL (the awaits are WebGPU-only), so
        // the patch is live before the first frame; void the promise.
        const renderer = viewer.three.renderer;
        if (renderer) void stats.init(renderer).catch((e) => console.warn('stats-gl GPU init failed:', e));
        // Override stats-gl's fixed top-left placement so it nests in the corner
        // perf box. Panels are absolutely positioned at top = id * 48px, so the
        // relative wrapper needs an explicit height to hug them; pointer-events:
        // none keeps the overlay non-interactive (the cluster button toggles it).
        const panelCount = stats.dom.children.length || 1;
        Object.assign(stats.dom.style, {
            position: 'relative', left: '0', top: '0',
            width: '90px', height: `${panelCount * STATS_PANEL_H}px`,
            zIndex: 'auto', pointerEvents: 'none',
        });
        const statsContainer = document.getElementById('fps-panel');
        (statsContainer ?? document.body).appendChild(stats.dom);

        const applyThemeColors = makeStatsThemer(stats);
        applyThemeColors();
        const offTheme = store.onIntent(INTENTS.UI_THEME_APPLY, applyThemeColors);
        const disposeToggle = installStatsToggle(viewer);
        return () => { offTheme(); disposeToggle(); };
    } catch (e) {
        console.warn('stats-gl failed to initialize:', e);
        return () => { /* nothing to dispose */ };
    }
}

/** Permanent stats toggle in the bottom cluster, styled like the other chips and
 *  placed just after the gnomon toggle (CSS `order`). Drives `body.stats-shown`,
 *  which CSS uses to reveal the corner perf box and hide the gnomon — so turning
 *  stats on "replaces" the gizmo in the lower-left. Single source of truth: the
 *  Shift+P palette command just clicks this button. */
function installStatsToggle(viewer: ViewerContext): () => void {
    const cluster = document.getElementById('bottom-cluster');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.id = 'stats-toggle';
    btn.className = 'stats-toggle';
    btn.title = 'Toggle performance stats';
    btn.setAttribute('aria-label', 'Toggle performance stats');
    const icon = document.createElement('i');
    icon.className = 'fas fa-chart-line';
    icon.setAttribute('aria-hidden', 'true');
    btn.appendChild(icon);

    const sync = (): void => {
        btn.setAttribute('aria-pressed', String(document.body.classList.contains('stats-shown')));
    };
    const onToggle = (e: MouseEvent): void => {
        e.preventDefault();
        document.body.classList.toggle('stats-shown');
        sync();
    };
    btn.addEventListener('click', onToggle);
    (cluster ?? document.body).appendChild(btn);

    document.body.classList.toggle('stats-shown', !!viewer.sceneMeta.showFps);
    sync();

    return () => { btn.removeEventListener('click', onToggle); btn.remove(); };
}

export function applyHelpStartIfEnabled(viewer: ViewerContext): void {
    // First-run clean view: collapse the side panels + hide the flyout. The
    // Help panel is no longer auto-shown — users open it via the ? button.
    if (!viewer.sceneMeta.helpStart) return;
    const colorbar = document.getElementById('colorbar-container');
    if (colorbar && !colorbar.classList.contains('collapsed')) colorbar.classList.add('collapsed');
    document.getElementById('controls-flyout')?.classList.add('is-hidden');
}

export function isHelpOpen(): boolean {
    const el = document.getElementById('help-panel');
    return !!(el && el.classList.contains('show'));
}

export function applyMobilePortraitCollapseIfApplicable(): void {
    if (window.innerWidth >= window.innerHeight) return;
    // Stats are off by default (and gated behind the cluster toggle), so portrait
    // only needs to collapse the colorbar + hide the controls flyout.
    const colorbar = document.getElementById('colorbar-container');
    if (colorbar && !colorbar.classList.contains('collapsed')) colorbar.classList.add('collapsed');
    document.getElementById('controls-flyout')?.classList.add('is-hidden');
}

/** One-shot: set every pane's initial collapsed/expanded state from scene
 *  config + viewport. Must run *before* `body.app-loading` is removed so
 *  the fade-in reveals the final state, not a mid-mutation state. */
export function settleInitialPaneState(viewer: ViewerContext): void {
    applyHelpStartIfEnabled(viewer);
    applyMobilePortraitCollapseIfApplicable();
}
